import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  DEFAULT_LANGUAGE,
  getTranslation,
  normalizeLanguage,
  setActiveLanguage,
} from '../translation';
import { CurrentUserContext } from './currentUserContext';

export const TranslationContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key, values, fallback) => fallback ?? key,
});

function getByPath(source, path) {
  return String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((value, part) => (value && typeof value === 'object' ? value[part] : undefined), source);
}

function interpolate(value, values) {
  if (typeof value !== 'string' || !values) {
    return value;
  }

  return value.replace(/\{\{(\w+)}}/g, (match, key) => (
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
  ));
}

function getTelegramLanguage(telegramUser) {
  return normalizeLanguage(telegramUser?.language_code || telegramUser?.languageCode);
}

function getCurrentUserLanguage(currentUser) {
  return normalizeLanguage(currentUser?.personal_data?.language);
}

export function TranslationProvider({ children, telegramUser }) {
  const { currentUser } = useContext(CurrentUserContext);
  const currentUserLanguage = getCurrentUserLanguage(currentUser);
  const telegramLanguage = getTelegramLanguage(telegramUser);
  const [selectedLanguage, setSelectedLanguageState] = useState(
    telegramLanguage || DEFAULT_LANGUAGE
  );
  const language = currentUserLanguage || selectedLanguage || telegramLanguage || DEFAULT_LANGUAGE;

  useEffect(() => {
    if (currentUserLanguage) {
      setSelectedLanguageState(currentUserLanguage);
    }
  }, [currentUserLanguage]);

  useEffect(() => {
    if (!currentUserLanguage && telegramLanguage) {
      setSelectedLanguageState((previousLanguage) => previousLanguage || telegramLanguage);
    }
  }, [currentUserLanguage, telegramLanguage]);

  useEffect(() => {
    setActiveLanguage(language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((nextLanguage) => {
    const normalizedLanguage = normalizeLanguage(nextLanguage) || DEFAULT_LANGUAGE;
    setSelectedLanguageState(normalizedLanguage);
    setActiveLanguage(normalizedLanguage);
  }, []);

  const t = useCallback(
    (key, values, fallback) => {
      const translation = getTranslation(language);
      const fallbackTranslation = getTranslation(DEFAULT_LANGUAGE);
      const translatedValue = getByPath(translation, key)
        ?? getByPath(fallbackTranslation, key)
        ?? fallback
        ?? key;

      return interpolate(translatedValue, values);
    },
    [language]
  );

  const dictionary = useMemo(() => getTranslation(language), [language]);
  const value = useMemo(
    () => ({
      ...dictionary,
      language,
      setLanguage,
      t,
    }),
    [dictionary, language, setLanguage, t]
  );

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  return useContext(TranslationContext);
}
